import { Div } from '@codesuma/baseline'
import { Item } from '../../services/list'
import { haptic } from '../../utils/haptics'
import style from './index.module.css'

// Only one row menu is open at a time, app-wide. The document-click listener
// each menu installs can't manage this on its own: long-pressing another row
// opens its menu with no click at all (the finger is still down), so the first
// row would stay open and lifted. Whoever opens closes whoever was open.
let closeOpenMenu: (() => void) | null = null

// readOnly (a public list's preview): the same row with nothing to act on — no
// trash, no menu, and the tick doesn't toggle.
export const ListItem = (item: Item, readOnly = false) => {
    const base = Div()
    base.addClass(style.base)
    if (readOnly) base.addClass(style.readOnly)
    // A section heading renders as a bold divider label: no tick circle (nothing
    // to complete) and no "Mark as done" in the menu. Rename, the trash button
    // and drag all work the same; deleting a heading keeps its items — they just
    // join the section above.
    const isHeading = item.kind === 'heading'
    if (isHeading) base.addClass(style.heading)
    if (item.checked) base.addClass(style.done)

    // Tick circle (left)
    const tick = Div()
    tick.addClass(style.tick)
    const tickCircle = Div()
    tickCircle.addClass(style.tickCircle)
    const check = Div() // checkmark whose stroke draws on; inherits the item's text color
    check.addClass(style.check)
    check.el.innerHTML = '<svg viewBox="0 0 100 100" aria-hidden="true"><polyline points="22,52 42,72 78,30"/></svg>'
    tickCircle.append(check)
    tick.append(tickCircle)

    // Title
    const title = Div(item.name)
    title.addClass(style.title)

    // Trash button (right) — a plain, unmistakable "delete this item"
    const trash = Div()
    trash.addClass(style.trash)
    const trashIcon = Div() // tinted to the item's text color via CSS mask
    trashIcon.addClass(style.trashIcon)
    trash.append(trashIcon)

    // Long-press dropdown (see below). Delete sits last so a finger sliding off
    // Rename doesn't land on it.
    const menu = Div()
    menu.addClass(style.menu)
    const editOption = Div('Rename')
    editOption.addClass(style.menuItem)
    const doneOption = Div() // label set on open: "Mark as done" / "Not done"
    doneOption.addClass(style.menuItem)
    const removeOption = Div('Delete')
    removeOption.addClass(style.menuItem, style.menuItemDanger)
    if (isHeading) menu.append(editOption, removeOption)
    else menu.append(editOption, doneOption, removeOption)

    if (!isHeading) base.append(tick)
    base.append(title)
    if (!readOnly) base.append(trash, menu)

    // --- Toggle done ---
    // The row owns its checked state so a tap can play its flourish immediately
    // (optimistically), independent of the data round-trip. setChecked is the one
    // place the `.done` look is applied — the page calls it too, so a remote
    // member's tick animates here as well.
    let checked = !!item.checked
    const setChecked = (next: boolean, flourish = true) => {
        if (next === checked) return // already there (e.g. our own tap echoing back)
        checked = next
        base.toggleClass(style.done, next)
        if (next && flourish) {
            tickCircle.removeClass(style.pop)
            void tickCircle.el.offsetWidth // restart the spring keyframe from the top
            tickCircle.addClass(style.pop)
            haptic()
        }
    }
    tickCircle.el.addEventListener('animationend', () => tickCircle.removeClass(style.pop))

    const isEditing = () => title.el.getAttribute('contenteditable') === 'true'
    const toggle = (e: Event) => {
        e.stopPropagation()
        if (isEditing()) return
        setChecked(!checked) // optimistic: spring + draw + haptic right on tap
        base.emit('toggle', { id: item.id })
    }
    // Only the circle toggles — a tap anywhere else on the row must not surprise
    // a first-time user by checking the item (see CLAUDE.md "Product principle").
    if (!readOnly) tick.el.onclick = toggle

    // Tactile press dip: the row nudges down a pixel while the tick is held,
    // then springs back. Drag and FLIP also drive base's transform, but a move past
    // threshold releases the press first, so they take over cleanly.
    let pressed = false
    const press = (down: boolean) => {
        if (down === pressed) return
        pressed = down
        base.style({
            transition: down ? 'transform 0.12s ease' : 'transform 0.2s ease',
            transform: down ? 'translateY(1px)' : 'translateY(0)',
        })
    }
    let pressX = 0
    let pressY = 0
    const pressDown = (e: PointerEvent) => {
        if (readOnly || isEditing()) return
        pressX = e.clientX
        pressY = e.clientY
        press(true)
    }
    const pressMove = (e: PointerEvent) => {
        if (pressed && (Math.abs(e.clientX - pressX) > 8 || Math.abs(e.clientY - pressY) > 8)) press(false)
    }
    const pressUp = () => press(false)
    ;[tick.el].forEach(el => {
        el.addEventListener('pointerdown', pressDown)
        el.addEventListener('pointermove', pressMove)
        el.addEventListener('pointerup', pressUp)
        el.addEventListener('pointercancel', pressUp)
    })

    // --- Lift / settle (shared by drag-to-reorder and the long-press menu) ---
    // The lift scale rides along on every transform the drag sets, so the row
    // keeps its raised look from the moment it's grabbed until it settles.
    const LIFT = 'scale(1.03)'
    const SETTLE = 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease, background 0.2s ease'

    // Held long enough to act on: raise the row so it reads as "grabbed".
    const lift = () => {
        pressed = false // the press dip shares this transform — stand it down
        base.addClass(style.lifted)
        base.style({ transform: LIFT, transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease', zIndex: '20' })
    }

    // Let go: the row eases back down into place instead of snapping. The raised
    // z-index has to outlive the glide or the row falls behind its neighbours
    // mid-flight.
    const drop = () => {
        base.removeClass(style.lifted, style.dragging)
        base.style({ transform: 'translateY(0) scale(1)', transition: SETTLE })
        setTimeout(() => base.style({ zIndex: '' }), 320)
    }

    // --- Dropdown menu (close on outside click while open, no leaked listeners) ---
    let menuOpen = false
    const onDocClick = () => closeMenu()
    const closeMenu = () => {
        menu.removeClass(style.menuOpen)
        base.removeClass(style.menuActive) // drop the row back to its normal stacking
        drop() // and back down from the lift the long press raised it to
        menuOpen = false
        if (closeOpenMenu === closeMenu) closeOpenMenu = null
        document.removeEventListener('click', onDocClick)
    }
    const openMenu = () => {
        closeOpenMenu?.() // whichever row was open loses its menu and its lift
        closeOpenMenu = closeMenu
        if (!isHeading) doneOption.text(checked ? 'Not done' : 'Mark as done')
        menu.addClass(style.menuOpen)
        // Each row is its own stacking context (will-change: transform), so the
        // open menu would paint behind later rows and taps would fall through to
        // them — lift this row above its siblings while the menu is open.
        base.addClass(style.menuActive)
        // Stay raised: the press already lifted the row (reorder.ts), and letting
        // it drop just as the menu opens reads as the gesture being cancelled.
        lift()
        menuOpen = true
        setTimeout(() => document.addEventListener('click', onDocClick), 0)
    }

    // --- Long-press opens the menu ---
    // Coexists with drag-to-reorder (which also long-presses, see reorder.ts):
    // the row lifts at 300ms and can be dragged from then on; holding STILL all
    // the way to here opens the menu instead. The gap between the two is
    // deliberately generous — someone who held on to move an item shouldn't get
    // a menu just for taking a moment to aim.
    const MENU_DELAY = 800
    let holdTimer: ReturnType<typeof setTimeout> | undefined
    let holdX = 0
    let holdY = 0
    base.el.addEventListener('pointerdown', (e: PointerEvent) => {
        if (readOnly || isEditing() || menuOpen) return
        holdX = e.clientX
        holdY = e.clientY
        clearTimeout(holdTimer)
        holdTimer = setTimeout(() => {
            if (base.el.classList.contains(style.dragging)) return // a drag won the press
            // Disarm the reorder controller's pending grab (it listens on the list
            // container, so a bubbled cancel reaches it) and release the press dip.
            base.el.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true }))
            // The finger is still down — swallow the click that fires on release so
            // it doesn't instantly close the menu via the document listener.
            const guard = (ev: Event) => ev.stopPropagation()
            base.el.addEventListener('click', guard, { capture: true, once: true })
            setTimeout(() => base.el.removeEventListener('click', guard, true), 600)
            haptic()
            openMenu()
        }, MENU_DELAY)
    })
    base.el.addEventListener('pointermove', (e: PointerEvent) => {
        if (Math.abs(e.clientX - holdX) > 8 || Math.abs(e.clientY - holdY) > 8) clearTimeout(holdTimer)
    })
    base.el.addEventListener('pointerup', () => clearTimeout(holdTimer))
    base.el.addEventListener('pointercancel', () => clearTimeout(holdTimer))
    // Keep the browser's own long-press context menu / text selection out of the way.
    base.el.addEventListener('contextmenu', (e) => e.preventDefault())

    // --- Rename (inline contenteditable) ---
    editOption.el.onclick = (e) => {
        e.stopPropagation()
        closeMenu()
        title.el.setAttribute('contenteditable', 'true')
        title.addClass(style.editing)
        title.el.focus()
        const range = document.createRange()
        range.selectNodeContents(title.el)
        const sel = window.getSelection()
        if (sel) {
            sel.removeAllRanges()
            sel.addRange(range)
        }
    }
    const saveEdit = () => {
        title.el.removeAttribute('contenteditable')
        title.removeClass(style.editing)
        const value = (title.el.textContent || '').trim()
        if (value && value !== item.name) base.emit('update', { id: item.id, name: value })
        else if (!value) title.text(item.name)
    }
    title.el.addEventListener('blur', saveEdit)
    title.el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            title.el.blur()
        }
        if (e.key === 'Escape') {
            title.text(item.name)
            title.el.blur()
        }
    })

    // --- Done / not done (menu route to the same toggle as the circle) ---
    doneOption.el.onclick = (e) => {
        closeMenu()
        toggle(e)
    }

    // --- Delete (trash button or menu) ---
    const remove = (e: Event) => {
        e.stopPropagation()
        closeMenu()
        base.emit('delete', { id: item.id })
    }
    trash.el.onclick = remove
    removeOption.el.onclick = remove

    // Ripple on press (not on click — the row is gone right after the delete).
    trash.el.addEventListener('pointerdown', () => {
        trash.removeClass(style.ripple)
        void trash.el.offsetWidth // restart the animation from the top
        trash.addClass(style.ripple)
    })
    trash.el.addEventListener('animationend', () => trash.removeClass(style.ripple))

    // --- Drag helpers (driven by the list container, see pages/list/reorder) ---
    return Object.assign(base, {
        item,
        setChecked,
        lift,
        drop,
        move(dy: number) {
            base.style({ transform: `translateY(${dy}px) ${LIFT}`, transition: 'none', zIndex: '20' })
        },
        shift(offset: number) {
            base.style({ transform: `translateY(${offset}px)`, transition: 'transform 0.16s ease' })
        },
        // Park the row at an offset with no animation — the "invert" half of the
        // drop FLIP, so it can glide from where the finger left it to its slot.
        park(dy: number) {
            base.style({ transform: `translateY(${dy}px) ${LIFT}`, transition: 'none', zIndex: '20' })
        },
        highlight() {
            base.addClass(style.dragging)
        },
    })
}
